<template>
  <div :class="bem.b()">
    <a-spin :spinning="loading">
      <div :class="bem.e('container')">
        <Toolbar
          :class="bem.e('toolbar-container')"
          :editor="editorRef"
          :default-config="props.toolbarConfig"
          :mode="props.mode"
        />
        <!--  -->
        <Editor
          :model-value="innerValue"
          :class="bem.e('editor-container')"
          :default-config="editorConfig"
          :mode="props.mode"
          @update:model-value="onEditorUpdate"
          @on-created="handleCreated"
          @on-change="handleChange"
          @on-destroyed="handleDestroyed"
          @on-focus="handleFocus"
          @on-blur="handleBlur"
          @custom-alert="customAlert"
        />
      </div>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, shallowRef, computed, watch } from 'vue'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { createNamespace } from '@ll-plus/utils'
import {
  richTextEditorProps,
  richTextEditorEmits,
  FALLBACK_IMAGE,
  type InsertPicType,
  type InsertVidType,
  type ImageElement,
} from './rich-text-editor'

import '@wangeditor/editor/dist/css/style.css' // 引入 css

import type { IDomEditor } from '@wangeditor/editor'

//@ts-ignore
defineOptions({ name: 'LlRichTextEditor' })

const bem = createNamespace('rich-text-editor')

const props = defineProps(richTextEditorProps)

const emits = defineEmits(richTextEditorEmits)

const editorRef = shallowRef()

// const innerValue = computed(() => props.value)
const innerValue = ref(props.value)

const loading = ref<boolean>(props.loading)

const allImages = ref<any>([])

const editorConfig = computed(() => {
  return {
    MENU_CONF: {
      uploadImage: {
        async onBeforeUpload(file: File) {
          if (props.onBeforeUploadImage) {
            const res = await props.onBeforeUploadImage(file)
            return res ? file : false
          }
          return file
        },

        async customUpload(file: File, insertFn: InsertPicType) {
          loading.value = true
          //图片上传接口调用
          try {
            const res = await props.customUploadImage(file)
            insertFn(res)
            loading.value = false
          } catch (error) {
            editorRef.value.alert('错误信息', props.uploadImageError)
            loading.value = false
          }
        },
        base64LimitSize: 5 * 1024,
      },
      insertImage: {
        async onInsertedImage(imageNode: ImageElement | null) {
          allImages.value.push(imageNode)
        },
      },
      uploadVideo: {
        async customUpload(file: File, insertFn: InsertVidType) {
          if (props.customUploadVideo) {
            loading.value = true
            //图片上传接口调用
            try {
              const res = await props.customUploadVideo(file)
              insertFn(res, FALLBACK_IMAGE)
              loading.value = false
            } catch (error) {
              editorRef.value.alert('错误信息', props.uploadImageError)
              loading.value = false
            }
          }
        },
      },
    },
    ...props.editorConfig,
  }
})

const handleCreated = (editor: IDomEditor) => {
  editorRef.value = editor
  allImages.value = editor.getElemsByType('image')
  // 编辑器创建完成后立即应用 disabled / loading 状态
  if (props.disabled || loading.value) {
    editor.disable()
  } else {
    editor.enable()
  }
  emits('created', editor)
}

const onEditorUpdate = (value: string) => {
  innerValue.value = value
  emits('update:value', value)
}

const handleChange = (editor: IDomEditor) => {
  // innerValue.value = editor.getHtml()
  emits('change', editor)
  // emits('update:value', innerValue.value)
}
const handleDestroyed = (editor: IDomEditor) => {
  emits('destroyed', editor)
}
const handleFocus = (editor: IDomEditor) => {
  emits('focus', editor)
}
const handleBlur = (editor: IDomEditor) => {
  emits('blur', editor)
}
const customAlert = (info, type) => {
  emits('alert', [info, type])
}

const getEditorImage = () => {
  const editor = editorRef.value
  if (editor == null) return
  return {
    allImages: allImages.value,
    currentImages: editor.getElemsByType('image'),
  }
}

// 组件销毁时，也及时销毁编辑器
onBeforeUnmount(() => {
  const editor = editorRef.value
  if (editor == null) return
  editor.destroy()
})

watch(
  () => props.loading,
  (val: boolean) => {
    loading.value = val
  },
)

watch(
  () => props.value,
  (newValue) => {
    if (newValue !== innerValue.value) {
      innerValue.value = newValue
    }
  },
  { deep: true },
)

watch(
  () => [props.disabled, loading.value],
  ([disabled, isLoading]) => {
    const editor = editorRef.value
    if (editor == null) return
    disabled || isLoading ? editor.disable() : editor.enable()
  },
)

defineExpose({
  editorRef,
  getEditorImage,
})
</script>
